import React, { useCallback, useEffect, useRef, useState } from "react";
import { Panel } from "@/components/common/Panel";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import KGraph from "@/components/graph/CytoGraph";
import { api } from "@/lib/api";
import { Save, Trash2, Bookmark, Radar } from "lucide-react";
import { toast } from "sonner";
import { TID } from "@/lib/testIds";

export default function KnowledgeGraph() {
    const [workspaces, setWorkspaces] = useState([]);
    const [wsId, setWsId] = useState(null);
    const [savedViews, setSavedViews] = useState([]);
    const [viewName, setViewName] = useState("");
    const [appliedView, setAppliedView] = useState(null);
    // Live graph state (filters / search / impact focus) reported up by CytoGraph.
    const graphState = useRef({ types: [], query: "", impact: null });

    const loadViews = useCallback(async () => {
        try {
            const { data } = await api.get("/graph/views");
            setSavedViews(data);
        } catch (e) { console.warn("Failed to load saved graph views", e); }
    }, []);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/workspaces");
            setWorkspaces(data);
            const withFindings = data.find((w) => (w.finding_count || 0) > 0);
            setWsId((withFindings || data[0])?.id || null);
        })();
        loadViews();
    }, [loadViews]);

    const onGraphState = useCallback((s) => { graphState.current = s; }, []);

    const saveView = async () => {
        if (!wsId) return;
        const name = viewName.trim() || `View ${savedViews.length + 1}`;
        const ws = workspaces.find((w) => w.id === wsId);
        try {
            await api.post("/graph/views", {
                name,
                workspace_id: wsId,
                workspace_name: ws?.name || "—",
                types: graphState.current.types,
                query: graphState.current.query,
                impact: graphState.current.impact,
            });
            setViewName("");
            await loadViews();
            toast.success(`Saved view "${name}"`);
        } catch (e) {
            toast.error("Could not save this view");
        }
    };

    const openView = (v) => {
        setWsId(v.workspace_id);
        setAppliedView({ key: `${v.id}:${Date.now()}`, types: v.types, query: v.query, impact: v.impact });
        toast.message(`Loaded view "${v.name}"`, {
            description: v.impact ? `Impact focus · ${v.impact.node_id} · ${v.impact.depth} hops` : "Filters and search restored",
        });
    };

    const removeView = async (v) => {
        try {
            await api.delete(`/graph/views/${v.id}`);
            await loadViews();
            toast.success("View removed");
        } catch (e) {
            toast.error("Could not remove this view");
        }
    };

    return (
        <div className="space-y-5">
            <Panel
                eyebrow="Authorization Knowledge Graph"
                title="Visual reasoning across the SAP authorization perimeter"
                action={
                    <Select value={wsId || ""} onValueChange={setWsId}>
                        <SelectTrigger data-testid="kg-workspace-select" className="w-[300px] h-8 bg-[var(--bg-surface-2)] border-white/10 text-xs">
                            <SelectValue placeholder="Select workspace" />
                        </SelectTrigger>
                        <SelectContent className="bg-[var(--bg-elevated)] border-cyan-500/20 text-slate-200">
                            {workspaces.map((w) => (
                                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            >
                <p className="text-sm text-slate-400">
                    The Knowledge Graph correlates ABAP programs, AUTHORITY-CHECK statements, authorization objects, SU24 proposals,
                    transaction codes, OData services, findings, and recommendations into a single explorable structure.
                    Select any node and trace its <span className="text-cyan-300">blast radius</span> to see exactly what a change
                    would touch, hop by hop.
                </p>

                <div className="mt-4 flex flex-col md:flex-row md:items-center gap-2">
                    <input
                        data-testid={TID.graphViewName}
                        value={viewName}
                        onChange={(e) => setViewName(e.target.value)}
                        placeholder="Save filters, search & impact focus as a view…"
                        className="field md:max-w-xs"
                    />
                    <button data-testid={TID.graphViewSave} onClick={saveView} disabled={!wsId} className="btn-primary flex items-center gap-1.5 disabled:opacity-50">
                        <Save size={11} /> Save View
                    </button>
                    {savedViews.length > 0 && (
                        <div className="flex flex-wrap gap-2 ml-2" data-testid="kg-saved-views">
                            {savedViews.map((v) => (
                                <div key={v.id} className="panel px-2 py-1 text-[11px] flex items-center gap-2">
                                    {v.impact ? <Radar size={10} className="text-amber-400" /> : <Bookmark size={10} className="text-cyan-400" />}
                                    <button data-testid={TID.graphViewOpen(v.name)} onClick={() => openView(v)} className="text-cyan-300 hover:text-cyan-200">{v.name}</button>
                                    <span className="text-slate-600">·</span>
                                    <span className="text-slate-500 font-mono">{v.workspace_name}</span>
                                    <button data-testid={TID.graphViewDelete(v.name)} onClick={() => removeView(v)} className="text-slate-500 hover:text-rose-400" aria-label={`Remove view ${v.name}`}>
                                        <Trash2 size={10} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </Panel>

            {wsId && <KGraph workspaceId={wsId} height={680} appliedView={appliedView} onStateChange={onGraphState} />}
            {!wsId && (
                <Panel eyebrow="Empty" title="No workspace selected">
                    <p className="text-sm text-slate-400">Create a workspace and run analysis to populate the graph.</p>
                </Panel>
            )}
        </div>
    );
}
